// Copyright 2026, University of Maryland, All Rights Reserved
import { useMemo, useState, type FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Info, Loader2, Plus } from 'lucide-react'

import { Button } from '@/components/ui'
import { toast } from 'sonner'
import { useServices } from '@/app-shell/services'
import { useAuth } from '@/subsystems/workspaces'
import {
  useCreateAgent,
  useAgents,
  useAgentTemplates,
  useInstantiateAgent,
  extractResourceConflicts,
  type AgentTemplate,
} from '@/subsystems/agents'
import { usePipelinesList } from '@/subsystems/pipelines'
import { getAllLayoutComponents } from '@/core/layout-registry'
import { slugify } from '@/subsystems/layout/utils/layout-utils'
import { NewAgentProgress } from './NewAgentProgress'
import { AgentConfigurationForm } from './AgentConfigurationForm'
import { AgentTypePicker } from './AgentTypePicker'
import { nextAgentId } from './newAgentId'
import { useSidebar } from '@/app-shell/components/SidebarContext'

const NONE_VALUE = '__none__'

/** True when a create failure was caused by a duplicate agent ID (string detail). */
const isAgentIdConflict = (err: unknown): boolean => {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  if (res?.status === 409) return true
  return typeof res?.data?.detail === 'string' && res.data.detail.includes('already exists')
}

const NewAgentPage: FC = () => {
  const navigate = useNavigate()
  const sidebar = useSidebar()
  const services = useServices()
  const { selectedOrganizationId, selectedWorkspaceId, isGlobalAdmin } = useAuth()
  const orgId = selectedOrganizationId ?? ''
  const workspaceId = selectedWorkspaceId ?? ''
  const client = services.getClient()

  const createAgent = useCreateAgent(client, orgId, workspaceId)
  const instantiate = useInstantiateAgent(client, orgId, workspaceId)
  const { data: agents = [] } = useAgents(client, orgId, workspaceId)
  const { data: templates = [], isLoading: templatesLoading } = useAgentTemplates(client, orgId, workspaceId)

  const [mode, setMode] = useState<'library' | 'custom'>('library')
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null)
  const [label, setLabel] = useState('')
  const [templateNameTouched, setTemplateNameTouched] = useState(false)
  const [agentId, setAgentId] = useState('')
  const [agentIdTouched, setAgentIdTouched] = useState(false)
  const [agentIdError, setAgentIdError] = useState<string | null>(null)

  // Custom-mode state
  const [description, setDescription] = useState('')
  const [pipelineId, setPipelineId] = useState<string>(NONE_VALUE)
  const [dashboardId, setDashboardId] = useState<string>(NONE_VALUE)
  const [submitting, setSubmitting] = useState(false)

  const { data: pipelines = {} } = usePipelinesList(orgId, workspaceId)

  const pipelineEntries = Object.values(pipelines)
  const dashboardOptions = useMemo(
    () => getAllLayoutComponents().filter((component) => component.display.category === 'agent-dashboard'),
    [],
  )

  if (!orgId || !workspaceId) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        Select a workspace to create an agent.
      </div>
    )
  }

  // The agent ID defaults to a slug of the label and tracks it as the user
  // types, until they edit the ID themselves — then it stays put.
  const handleLabelChange = (value: string) => {
    setLabel(value)
    if (!agentIdTouched) setAgentId(slugify(value))
    // In library mode the conflict error belongs to the name; clear it on edit.
    if (mode === 'library' && agentIdError) setAgentIdError(null)
  }

  const handleAgentIdChange = (value: string) => {
    setAgentIdTouched(true)
    setAgentId(value)
    if (agentIdError) setAgentIdError(null)
  }

  const handleSelectTemplate = (t: AgentTemplate) => {
    setMode('library')
    setSelectedTemplate(t)
    if (!templateNameTouched || !label.trim()) setLabel(t.label)
    setAgentIdError(null)
  }

  const handleContinue = () => {
    setAgentIdError(null)
    setLabel('')
    setAgentId('')
    setAgentIdTouched(false)
    setDescription('')
    setPipelineId(NONE_VALUE)
    setDashboardId(NONE_VALUE)
    setStep(2)
  }

  const handleSubmit = async () => {
    const trimmedLabel = label.trim()
    const trimmedId = mode === 'library'
      ? nextAgentId(trimmedLabel, agents.map(agent => agent.agentId))
      : agentId.trim()
    if (!trimmedLabel || !trimmedId) return
    setSubmitting(true)
    setAgentIdError(null)
    try {
      if (mode === 'library' && selectedTemplate) {
        await instantiate.mutateAsync({
          agentId: trimmedId,
          label: trimmedLabel,
          sourceAgentId: selectedTemplate.agentId,
        })
      } else {
        await createAgent.mutateAsync({
          agentId: trimmedId,
          label: trimmedLabel,
          description: description.trim() || null,
          pipelineId: pipelineId === NONE_VALUE ? null : pipelineId,
          dashboardId: dashboardId === NONE_VALUE ? null : dashboardId,
        })
      }
      toast.success(
        mode === 'library' && selectedTemplate
          ? `“${trimmedLabel}” created from ${selectedTemplate.label}`
          : `“${trimmedLabel}” created`,
        {
          position: 'bottom-center',
          duration: 3400,
          style: {
            background: '#0f172a',
            border: 0,
            borderRadius: 12,
            boxShadow: '0 16px 40px rgba(2,8,23,.35)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 500,
            padding: '11px 16px',
          },
        },
      )
      sidebar?.setNewAgentId(trimmedId)
      navigate(`/agents/${trimmedId}`)
    } catch (err) {
      // Structured conflicts only come from instantiate (library mode).
      const conflicts = extractResourceConflicts(err)
      if (conflicts) {
        if (conflicts.some(c => c.field === 'agentId')) {
          setAgentIdError(
            `An agent named "${trimmedLabel}" already exists in this workspace. Choose a different name.`,
          )
        } else {
          toast.error('Failed to create agent', {
            description: conflicts.map(c => c.message).join(' '),
          })
        }
      } else if (isAgentIdConflict(err)) {
        setAgentIdError(`An agent with ID "${trimmedId}" already exists in this workspace.`)
      } else {
        toast.error('Failed to create agent', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const canContinue = mode === 'custom' || (selectedTemplate !== null && label.trim().length > 0)
  const canSubmit = label.trim().length > 0 && agentId.trim().length > 0

  return (
    <div className={`docreview-create-agent-page mx-auto px-6 pt-6 ${step === 1 ? 'max-w-[920px] pb-12' : 'max-w-[780px] pb-20'}`}>
      <div className="mb-[26px] flex items-center justify-between gap-6">
        <Button className="h-[38px] gap-[7px] rounded-[9px] border-[#e2e8f0] bg-white px-[14px] text-sm font-medium text-[#334155] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-[#cbd5e1] hover:bg-[#f8fafc]" variant="outline" size="sm" onClick={() => step === 2 ? setStep(1) : navigate(-1)}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <NewAgentProgress step={step} />
      </div>

      <div>
        <h1 className="text-[30px] font-bold leading-[1.15] tracking-[-0.02em] text-[#0f172a]">Create New Agent</h1>
        <p className="mt-2 text-base leading-[1.5] text-[#64748b]">
          {step === 1
            ? 'Choose an agent type from the library, or create a custom agent.'
            : 'Configure a custom agent for this workspace.'}
        </p>
      </div>

      {step === 1 ? (
        <div data-testid="agent-options-panel" className="mt-7 space-y-4 rounded-[14px] border border-[#e2e8f0] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <>
            {templatesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : templates.length === 0 && !isGlobalAdmin ? (
              <p className="text-sm text-muted-foreground py-4">
                No agent types are configured for this application yet.
              </p>
            ) : (
              <AgentTypePicker
                templates={templates}
                selectedTemplate={selectedTemplate}
                customSelected={mode === 'custom'}
                isGlobalAdmin={isGlobalAdmin}
                name={label}
                onNameChange={value => { setLabel(value); setTemplateNameTouched(true); setAgentIdError(null) }}
                onSelectTemplate={handleSelectTemplate}
                onSelectCustom={() => { setSelectedTemplate(null); setMode('custom') }}
              />
            )}
          </>
        </div>
      ) : (
        <AgentConfigurationForm
          label={label}
          agentId={agentId}
          description={description}
          pipelineId={pipelineId}
          dashboardId={dashboardId}
          agentIdError={agentIdError}
          workspaceId={workspaceId}
          agentIdSynced={!agentIdTouched}
          pipelines={[{ id: NONE_VALUE, label: 'None' }, ...pipelineEntries.map(pipeline => ({ id: pipeline.id, label: pipeline.name || pipeline.id }))]}
          dashboards={[{ id: NONE_VALUE, label: 'None' }, ...dashboardOptions.map(option => ({ id: option.id, label: option.display.label }))]}
          onLabelChange={handleLabelChange}
          onAgentIdChange={handleAgentIdChange}
          onDescriptionChange={setDescription}
          onPipelineChange={setPipelineId}
          onDashboardChange={setDashboardId}
        />
      )}

      <div className="mt-[22px] flex items-center gap-2.5">
        {step === 2 && !canSubmit && (
          <div className="flex items-center gap-[7px] text-[13.5px] text-[#94a3b8]"><Info className="size-3.5" />Enter a label and agent ID to create</div>
        )}
        <div className="flex-1" />
        <Button className={`${step === 2 ? 'h-[42px] px-5' : 'h-10 px-[18px]'} rounded-[9px] border-[#e2e8f0] bg-white text-sm font-medium text-[#334155] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-[#cbd5e1] hover:bg-[#f8fafc]`} variant="outline" onClick={() => navigate(-1)} disabled={submitting}>
          Cancel
        </Button>
        <Button
          data-testid={step === 1 ? 'agent-continue' : 'agent-create'}
          className={`${step === 2 ? 'h-[42px] px-[22px]' : 'h-10 px-5'} rounded-[9px] bg-[#111827] text-sm font-medium text-white shadow-[0_1px_2px_rgba(0,0,0,0.08)] transition-[background,opacity,transform] duration-150 hover:bg-[#1f2937] active:scale-[0.97]`}
          onClick={step === 1 ? (mode === 'library' ? handleSubmit : handleContinue) : handleSubmit}
          disabled={step === 1 ? !canContinue : !canSubmit || submitting}
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {step === 1 && mode === 'custom'
            ? <><span>Continue</span><ArrowRight className="ml-2 size-4" /></>
            : <><Plus className="mr-2 size-4" />Create Agent</>}
        </Button>
      </div>
    </div>
  )
}

export default NewAgentPage
